import React from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import {
  checkboxControlSx,
  choiceControlLabelSx,
  fieldSx,
  FormSelectField,
  FormTextField,
  radioControlSx,
  SectionShell,
} from "./memberWorkflowShared";

export type MemberBillingDiscountValue = {
  enabled: boolean;
  code: string;
  description: string;
  amount: string;
};

export type MemberBillingValue = {
  fullTimeEmployees: string;
  partTimeEmployees: string;
  membershipTypeId: string;
  paymentSchedule: string;
  paymentMethod: string;
  billingResponsibility: string;
  selectedAdditionalCostIds: string[];
  membershipDiscount: MemberBillingDiscountValue;
  autoRenew: boolean;
  emailCommunicationConsentConfirmed: boolean;
};

export type MemberBillingOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type MemberBillingSummary = {
  code: string;
  description: string;
  amountLabel: string;
  /**
   * Spec §7 (proration verbiage): set when annual prorated signup pricing
   * lowers the first-year price, so the step shows BOTH numbers instead of
   * silently substituting the prorated one. Absent (or prorated == base)
   * -> the single amountLabel renders exactly as before.
   */
  proration?: {
    proratedAmountLabel: string;
    regularAmountLabel: string;
  };
};

export type MemberBillingAddOn = {
  id: string;
  code: string;
  name: string;
  description: string;
  amountLabel: string;
};

export type MemberBillingOnboardingFee = {
  code: string;
  description: string;
  amountLabel: string;
};

export type MemberBillingResponsibilityOption = {
  value: string;
  title: string;
  description: string;
  /**
   * Step-4 spec: the "Invoice created for Internal Use Not Sent to Member"
   * sub-lines render bold (members-create-step4.md lines 92-94, 251, 322).
   */
  descriptionBold?: boolean;
};

export const chamberGroupInsuranceAutomaticActivationDescription =
  "The member will automatically become active when this selection is saved. The invoice is for internal use and will not be sent to the member.";

export const chamberGroupInsuranceExistingInvoiceDescription =
  "For a pending member, saving this option replaces the current unpaid membership invoice with an internal Johnson Group invoice and automatically activates the member. If payment activity exists, you’ll be asked to review it first. The invoice is not sent to the member.";

export type MemberBillingErrors = Partial<
  Record<
    | "employeeCount"
    | "membershipTypeId"
    | "membershipDiscountCode"
    | "membershipDiscountDescription"
    | "membershipDiscountAmount"
    | "billingResponsibility"
    | "paymentMethod"
    | "autoRenew"
    | "emailCommunicationConsentConfirmed",
    string
  >
>;

export type MemberBillingAdjustmentConfig = {
  enabled: boolean;
  hasChanges: boolean;
  submitting?: boolean;
  error?: string;
  blockedReason?: string;
  onEnabledChange: (enabled: boolean) => void;
  onSubmit: () => void;
};

export type MemberBillingScheduledChange = {
  statusLabel: string;
  currentMembershipName: string;
  scheduledMembershipName: string;
  effectiveDateLabel: string;
  actionRequiredReason?: string;
};

export type MemberBillingStepProps = {
  value: MemberBillingValue;
  membershipOptions: MemberBillingOption[];
  selectedMembershipSummary: MemberBillingSummary;
  paymentScheduleOptions: MemberBillingOption[];
  paymentMethodOptions?: MemberBillingOption[];
  paymentMethodTitle?: string;
  paymentMethodDescription?: string;
  billingOptions: MemberBillingResponsibilityOption[];
  addOns: MemberBillingAddOn[];
  oneTimeOnboardingFee?: MemberBillingOnboardingFee;
  addOnsTotalLabel: string;
  invoiceTotalLabel: string;
  // BILL-002/003: monthly memberships are invoiced once for the full
  // membership total (12 credit-card installments), while add-on benefits go
  // on a second full-amount invoice. The parent passes the effective schedule
  // and the two split totals so the copy below can be honest per schedule.
  isMonthlySchedule: boolean;
  membershipInvoiceTotalLabel: string;
  addOnInvoiceTotalLabel: string;
  showPaymentSchedule: boolean;
  showPaymentMethod?: boolean;
  paymentScheduleDescription?: string;
  monthlyPaymentsAvailable?: boolean;
  membershipDiscountEnabled: boolean;
  membershipDiscountAvailable?: boolean;
  autoRenewAvailable?: boolean;
  /**
   * Update-only membership adjustment behavior. Create Member omits this
   * object and retains the existing fully-editable billing workflow.
   */
  membershipAdjustment?: MemberBillingAdjustmentConfig;
  scheduledMembershipChange?: MemberBillingScheduledChange;
  disabled?: boolean;
  membershipPlaceholder?: string;
  errors?: MemberBillingErrors;
  onFullTimeEmployeesChange: (value: string) => void;
  onPartTimeEmployeesChange: (value: string) => void;
  onMembershipTypeChange: (value: string) => void;
  onPaymentScheduleChange: (value: string) => void;
  onPaymentMethodChange?: (value: string) => void;
  onMembershipDiscountChange: (
    value: Partial<MemberBillingDiscountValue>
  ) => void;
  onAdditionalCostToggle: (id: string, checked: boolean) => void;
  onBillingResponsibilityChange: (value: string) => void;
  onAutoRenewChange: (checked: boolean) => void;
  onEmailConsentChange: (checked: boolean) => void;
};

const centeredControlLabelSx = {
  ...choiceControlLabelSx,
  alignItems: "center",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    lineHeight: 1.5,
    paddingTop: 0,
  },
};

const fullWidthChoiceControlLabelSx = {
  ...choiceControlLabelSx,
  alignItems: "flex-start",
  width: "100%",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    paddingTop: "6px",
    width: "100%",
  },
};

type InlineBillingTextFieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => void;
};

const InlineBillingTextField = ({
  id,
  label,
  value,
  placeholder,
  disabled,
  error,
  onChange,
}: InlineBillingTextFieldProps) => (
  <TextField
    id={id}
    aria-label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    fullWidth
    error={!!error}
    helperText={error}
    disabled={disabled}
    sx={fieldSx}
  />
);

const toNumericInput = (value: string) => value.replace(/[^0-9]/g, "");

const toCurrencyInput = (value: string) => value.replace(/[^0-9.]/g, "");

const getPaymentScheduleLabel = (option: MemberBillingOption) =>
  option.label || option.value;

const MemberBillingStep = ({
  value,
  membershipOptions,
  selectedMembershipSummary,
  paymentScheduleOptions,
  paymentMethodOptions = [],
  paymentMethodTitle = "Select Payment Method For The One-Time Onboarding Fee",
  paymentMethodDescription = "The onboarding fee must be paid even when the membership itself has no charge. Select one of the payment methods enabled in Chamber Set Up.",
  billingOptions,
  addOns,
  oneTimeOnboardingFee,
  addOnsTotalLabel,
  invoiceTotalLabel,
  isMonthlySchedule,
  membershipInvoiceTotalLabel,
  addOnInvoiceTotalLabel,
  showPaymentSchedule,
  showPaymentMethod = false,
  paymentScheduleDescription,
  monthlyPaymentsAvailable = true,
  membershipDiscountEnabled,
  membershipDiscountAvailable = true,
  autoRenewAvailable = true,
  membershipAdjustment,
  scheduledMembershipChange,
  disabled,
  membershipPlaceholder = "Select Membership Type",
  errors = {},
  onFullTimeEmployeesChange,
  onPartTimeEmployeesChange,
  onMembershipTypeChange,
  onPaymentScheduleChange,
  onPaymentMethodChange,
  onMembershipDiscountChange,
  onAdditionalCostToggle,
  onBillingResponsibilityChange,
  onAutoRenewChange,
  onEmailConsentChange,
}: MemberBillingStepProps) => {
  const billingControlsDisabled =
    !!disabled ||
    !!membershipAdjustment?.submitting ||
    (!!membershipAdjustment && !membershipAdjustment.enabled);
  const addOnsAreReadOnly = !!membershipAdjustment;
  const usesThirdPartyBilling =
    value.billingResponsibility === "affiliatedPrimaryCompany" ||
    value.billingResponsibility === "chamberGroupInsurance";
  const effectivePaymentScheduleDescription =
    paymentScheduleDescription ||
    (usesThirdPartyBilling
      ? "Select the membership billing schedule below. Third-party billing does not require the member to provide a credit card."
      : "Select the member payment option below. Monthly payments are made via credit card only.");
  const showSeparateAddOnInvoice =
    !membershipAdjustment && value.selectedAdditionalCostIds.length > 0;

  return (
    <div className="space-y-10">
    <SectionShell
      id="member-size"
      title="Member Size Matters"
      description="Get to know the size of this member. Enter the number of full-time and part-time employees."
      variant="card"
      className={
        membershipAdjustment && !membershipAdjustment.enabled
          ? "!border-gray-300 !bg-gray-100"
          : ""
      }
    >
      <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
        <FormTextField
          id="step4-full-time-employees"
          label="Full-Time Employees"
          value={value.fullTimeEmployees}
          onChange={(nextValue) =>
            onFullTimeEmployeesChange(toNumericInput(nextValue))
          }
          placeholder="Enter the number of full time employees in the company"
          error={errors.employeeCount}
          disabled={billingControlsDisabled}
        />
        <FormTextField
          id="step4-part-time-employees"
          label="Part-Time Employees"
          value={value.partTimeEmployees}
          onChange={(nextValue) =>
            onPartTimeEmployeesChange(toNumericInput(nextValue))
          }
          placeholder="Enter the number of part time employees in the company"
          disabled={billingControlsDisabled}
        />
      </div>
    </SectionShell>

    <SectionShell
      id="type-of-membership"
      title="Type of Membership"
      description="Select the membership this member has chosen from the drop-down below. The membership investment will automatically populate."
      variant="card"
      className={
        membershipAdjustment && !membershipAdjustment.enabled
          ? "!border-gray-300 !bg-gray-100"
          : ""
      }
    >
      <FormSelectField
        id="step4-membership-type"
        label="Membership Type"
        value={value.membershipTypeId}
        onChange={onMembershipTypeChange}
        options={membershipOptions}
        placeholder={membershipPlaceholder}
        description="Membership type is automatically populated from your chamber set up"
        error={errors.membershipTypeId}
        required
        disabled={billingControlsDisabled}
      />
      <div className="mt-8 grid grid-cols-1 items-end gap-4 text-sm text-black lg:grid-cols-[120px_minmax(0,1fr)_220px]">
        <div>
          <p className="text-2xl font-bold text-black">
            {selectedMembershipSummary.code || "-"}
          </p>
        </div>
        <div>
          <p className="text-base font-bold text-black">
            Company Membership Investment Amount
          </p>
          <p className="mt-1 leading-6">
            {selectedMembershipSummary.description ||
              "Investment amount is automatically populated based on the membership selected."}
          </p>
        </div>
        {selectedMembershipSummary.proration ? (
          <div className="text-black lg:text-right">
            <p className="text-3xl font-bold">
              {selectedMembershipSummary.proration.proratedAmountLabel}
            </p>
            <p className="text-base font-bold leading-6">
              for your first year, then{" "}
              {selectedMembershipSummary.proration.regularAmountLabel} /annual
              afterwards
            </p>
          </div>
        ) : (
          <p className="text-3xl font-bold text-black lg:text-right">
            {selectedMembershipSummary.amountLabel}
          </p>
        )}
      </div>
      {selectedMembershipSummary.proration && (
        <p className="mt-3 text-sm leading-6 text-black">
          Regular price:{" "}
          {selectedMembershipSummary.proration.regularAmountLabel} /annual.
          Your chamber has prorated your first-year membership fee based on
          the month you are joining — you will only be charged{" "}
          {selectedMembershipSummary.proration.proratedAmountLabel} for this
          year.
        </p>
      )}
      {membershipAdjustment?.enabled && (
        <div className="mt-8 flex justify-center">
          <Button
            type="button"
            variant="contained"
            disabled={
              !!disabled ||
              membershipAdjustment.submitting ||
              !membershipAdjustment.hasChanges
            }
            onClick={membershipAdjustment.onSubmit}
            sx={{
              backgroundColor: "#E4A927",
              color: "white",
              fontWeight: 900,
              minWidth: 230,
              textTransform: "none",
              "&:hover": { backgroundColor: "#C58C16" },
            }}
          >
            {membershipAdjustment.submitting
              ? "Updating Membership Type..."
              : "Update Membership Type"}
          </Button>
        </div>
      )}
    </SectionShell>

    {membershipAdjustment && (
      <SectionShell
        id="adjust-membership-type"
        title="Adjust Membership Type"
        description={
          membershipAdjustment.enabled
            ? "Make the required changes, then select Update Membership Type. The current invoice and payment history will determine whether the change is applied now or scheduled for the next renewal."
            : "Enable editing to update this member's membership and billing details. Add-on benefits are view-only and will not be charged again."
        }
        variant="card"
      >
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-black">
            Allow Membership Type Change
          </span>
          <Switch
            checked={membershipAdjustment.enabled}
            disabled={
              disabled ||
              membershipAdjustment.submitting ||
              !!membershipAdjustment.blockedReason
            }
            inputProps={{ "aria-label": "Allow membership type change" }}
            onChange={(event) =>
              membershipAdjustment.onEnabledChange(event.target.checked)
            }
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#16BE7E" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#16BE7E",
              },
            }}
          />
          <span
            className={`text-xs font-bold uppercase tracking-wide ${
              membershipAdjustment.enabled ? "text-green-700" : "text-gray-500"
            }`}
          >
            {membershipAdjustment.enabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        {membershipAdjustment.error && (
          <FormHelperText error className="mt-3">
            {membershipAdjustment.error}
          </FormHelperText>
        )}
        {membershipAdjustment.blockedReason && (
          <FormHelperText className="mt-3 !text-amber-800">
            {membershipAdjustment.blockedReason}
          </FormHelperText>
        )}
      </SectionShell>
    )}

    {scheduledMembershipChange && (
      <SectionShell
        id="scheduled-membership-change"
        title="Scheduled Membership Change"
        description="This member already has a membership change scheduled for their existing renewal date."
        variant="card"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
            {scheduledMembershipChange.statusLabel}
          </span>
          <span className="text-sm font-bold text-black">
            Effective {scheduledMembershipChange.effectiveDateLabel}
          </span>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
              Current Membership
            </p>
            <p className="mt-1 font-bold text-black">
              {scheduledMembershipChange.currentMembershipName}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
              Scheduled Membership
            </p>
            <p className="mt-1 font-bold text-black">
              {scheduledMembershipChange.scheduledMembershipName}
            </p>
          </div>
        </div>
        {scheduledMembershipChange.actionRequiredReason && (
          <p className="mt-4 text-sm font-bold leading-6 text-red-700">
            Action required: {scheduledMembershipChange.actionRequiredReason}
          </p>
        )}
      </SectionShell>
    )}

    {showPaymentSchedule && (
      <SectionShell
        id="membership-payment-option"
        title="Membership Payment Option"
        description={effectivePaymentScheduleDescription}
        variant="card"
      >
        <RadioGroup
          row
          value={value.paymentSchedule}
          onChange={(event) => onPaymentScheduleChange(event.target.value)}
          className="grid grid-cols-1 gap-x-12 gap-y-2 sm:grid-cols-2"
        >
          {paymentScheduleOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={billingControlsDisabled || option.disabled}
              sx={centeredControlLabelSx}
              control={<Radio sx={radioControlSx} />}
              label={
                <span className="text-lg font-bold text-black">
                  {getPaymentScheduleLabel(option)}
                </span>
              }
            />
          ))}
        </RadioGroup>
      </SectionShell>
    )}

    {membershipDiscountEnabled && (
      <SectionShell
        id="membership-discount"
        title="Membership Discount Option is Enabled for (Internal Use Only)"
        description="To apply a membership discount for this member, check box, enter the discount code and description as you would like it to appear on the invoice, then enter the discount amount. The discount will automatically be applied and shown as a separate line item on the invoice. If not selected no discount description or amount will show on invoice."
        variant="card"
      >
        <FormControlLabel
          sx={centeredControlLabelSx}
          control={
            <Checkbox
              checked={value.membershipDiscount.enabled}
              disabled={
                billingControlsDisabled ||
                (!membershipDiscountAvailable &&
                  !value.membershipDiscount.enabled)
              }
              onChange={(event) =>
                onMembershipDiscountChange({
                  enabled: membershipDiscountAvailable
                    ? event.target.checked
                    : false,
                })
              }
              sx={checkboxControlSx}
            />
          }
          label={
            <span className="text-sm font-bold text-black">
              Select to allow this member to receive a membership discount
            </span>
          }
        />
        {!membershipDiscountAvailable && (
          <FormHelperText className="!text-amber-800">
            Membership discounts are no longer enabled in Chamber Set Up.
            Clear the saved discount to continue; it cannot be re-enabled here.
          </FormHelperText>
        )}
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-[240px_minmax(0,1fr)_240px]">
          <InlineBillingTextField
            id="membership-discount-code"
            label="Membership Discount Code"
            value={value.membershipDiscount.code}
            onChange={(nextValue) =>
              onMembershipDiscountChange({ code: nextValue })
            }
            placeholder="Enter Membership Discount Code"
            disabled={
              billingControlsDisabled ||
              !membershipDiscountAvailable ||
              !value.membershipDiscount.enabled
            }
            error={errors.membershipDiscountCode}
          />
          <InlineBillingTextField
            id="membership-discount-description"
            label="Membership Discount Description"
            value={value.membershipDiscount.description}
            onChange={(nextValue) =>
              onMembershipDiscountChange({ description: nextValue })
            }
            placeholder="Enter Membership Discount Description"
            disabled={
              billingControlsDisabled ||
              !membershipDiscountAvailable ||
              !value.membershipDiscount.enabled
            }
            error={errors.membershipDiscountDescription}
          />
          <InlineBillingTextField
            id="membership-discount-amount"
            label="Amount"
            value={value.membershipDiscount.amount}
            onChange={(nextValue) =>
              onMembershipDiscountChange({
                amount: toCurrencyInput(nextValue),
              })
            }
            placeholder="Enter Discount Amount"
            disabled={
              billingControlsDisabled ||
              !membershipDiscountAvailable ||
              !value.membershipDiscount.enabled
            }
            error={errors.membershipDiscountAmount}
          />
        </div>
      </SectionShell>
    )}

    {oneTimeOnboardingFee && !membershipAdjustment && (
      <SectionShell
        id="one-time-onboarding-fee"
        title="One-Time Onboarding Fee For New Members"
        description="This taxable fee is charged once for this new membership application. Membership discounts and proration do not reduce it, and it is not included in future renewals. For monthly memberships, the full onboarding fee is collected with the first payment rather than split across installments."
        variant="card"
      >
        <div className="grid grid-cols-1 items-start gap-4 border-y border-gray-200 py-5 text-sm text-black md:grid-cols-[160px_minmax(0,1fr)_220px]">
          <span className="font-bold">
            {oneTimeOnboardingFee.code || "ONBOARDING"}
          </span>
          <span>
            <span className="block font-bold">
              {oneTimeOnboardingFee.description || "One-Time Onboarding Fee"}
            </span>
            <span className="mt-1 block leading-6">
              Charged in full on this application only.
            </span>
          </span>
          <span className="text-lg font-bold md:text-right">
            {oneTimeOnboardingFee.amountLabel}
          </span>
        </div>
      </SectionShell>
    )}

    <SectionShell
      id="additional-membership-add-ons"
      title="Additional Membership Benefit Add-ons and Fees"
      description={
        addOnsAreReadOnly
          ? "Enabled add-on benefits from Chamber Set Up are shown for reference. Checked benefits are currently attached to this member. They cannot be changed here or added to a replacement membership invoice."
          : "Only the additional benefits you have created and enabled in your Chamber Setup will appear below. You can update, enable, or disable these options at any time by returning to your Chamber Setup."
      }
      variant="card"
    >
      {addOnsAreReadOnly && (
        <span className="mb-4 inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-gray-700">
          View only
        </span>
      )}
      <div className="border-t border-gray-200">
        {addOns.length === 0 ? (
          <div className="py-5 text-sm text-black">
            {addOnsAreReadOnly
              ? "No enabled or previously selected additional membership benefit add-ons are available."
              : "No additional membership benefit add-ons are configured in Membership Settings."}
          </div>
        ) : (
          addOns.map((addOn) => {
            const addOnRow = (
              <>
              <span className="flex items-start md:justify-center">
                <Checkbox
                  checked={value.selectedAdditionalCostIds.includes(addOn.id)}
                  disabled={disabled || addOnsAreReadOnly}
                  onChange={(event) =>
                    onAdditionalCostToggle(addOn.id, event.target.checked)
                  }
                  sx={checkboxControlSx}
                />
              </span>
              <span className="font-bold">{addOn.code || "-"}</span>
              <span>
                <span className="block font-bold">{addOn.name}</span>
                <span className="mt-1 block leading-6">
                  {addOn.description || "Additional membership benefit add-on."}
                </span>
              </span>
              <span className="font-bold md:text-right">
                {addOn.amountLabel}
              </span>
              </>
            );
            const rowClassName =
              "grid grid-cols-1 gap-4 border-b border-gray-200 py-5 text-sm text-black md:grid-cols-[56px_90px_minmax(0,1fr)_180px]";

            return addOnsAreReadOnly ? (
              <div key={addOn.id} className={rowClassName}>
                {addOnRow}
              </div>
            ) : (
              <label
                key={addOn.id}
                className={`${rowClassName} cursor-pointer`}
              >
                {addOnRow}
              </label>
            );
          })
        )}
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 text-black md:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <p className="text-base font-bold">
            {addOnsAreReadOnly
              ? "Current Benefit Add-ons Investment"
              : "Total Benefit Add-ons Investment"}
          </p>
          <p className="mt-1 text-sm">
            {addOnsAreReadOnly
              ? "Only checked, currently attached add-ons are included in this reference total. Add-ons are excluded from every invoice produced by this membership change."
              : "Investment amount is automatically populated based on the add-on benefits selected."}
          </p>
        </div>
        <p className="text-3xl font-bold md:text-right">{addOnsTotalLabel}</p>
      </div>
    </SectionShell>

    <SectionShell
      id="billing-details"
      title="Select the Billing Details the Member has chosen."
      description="Select who will be paying for this member's membership. Must select one option below:"
      variant="card"
    >
      <RadioGroup
        value={value.billingResponsibility}
        onChange={(event) => onBillingResponsibilityChange(event.target.value)}
        className="space-y-3"
      >
        {billingOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            disabled={billingControlsDisabled}
            sx={fullWidthChoiceControlLabelSx}
            control={<Radio sx={radioControlSx} />}
            label={
              <span className="block text-black">
                <span className="block text-sm font-bold">{option.title}</span>
                <span
                  className={`mt-1 block text-sm leading-6${
                    option.descriptionBold ? " font-bold" : ""
                  }`}
                >
                  {option.description}
                </span>
              </span>
            }
          />
        ))}
      </RadioGroup>
      {errors.billingResponsibility && (
        <FormHelperText error>{errors.billingResponsibility}</FormHelperText>
      )}
    </SectionShell>

    {showPaymentMethod && (
      <SectionShell
        id="membership-onboarding-fee-payment-method"
        title={paymentMethodTitle}
        description={paymentMethodDescription}
        variant="card"
      >
        {paymentMethodOptions.length > 0 ? (
          <RadioGroup
            value={value.paymentMethod}
            onChange={(event) => onPaymentMethodChange?.(event.target.value)}
            className="grid grid-cols-1 gap-x-12 gap-y-2 sm:grid-cols-2"
          >
            {paymentMethodOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                disabled={billingControlsDisabled || option.disabled}
                sx={centeredControlLabelSx}
                control={<Radio sx={radioControlSx} />}
                label={
                  <span className="text-base font-bold text-black">
                    {option.label}
                  </span>
                }
              />
            ))}
          </RadioGroup>
        ) : (
          <p className="text-sm leading-6 text-amber-800">
            No eligible payment methods are enabled in Chamber Set Up.
          </p>
        )}
        {errors.paymentMethod && (
          <FormHelperText error>{errors.paymentMethod}</FormHelperText>
        )}
      </SectionShell>
    )}

    <div
      id="membership-billing-confirmations"
      className="flex flex-col gap-4 px-6 text-black"
    >
        <FormControlLabel
          sx={centeredControlLabelSx}
          control={
            <Checkbox
              checked={value.autoRenew}
              disabled={
                billingControlsDisabled ||
                (!autoRenewAvailable && !value.autoRenew)
              }
              onChange={(event) => onAutoRenewChange(event.target.checked)}
              sx={checkboxControlSx}
            />
          }
          label={
            <span className="text-base text-black">
              Member has chosen to automatically renew membership. Auto-renew
              applies to the membership only; add-on benefits are never
              auto-renewed.
            </span>
          }
        />
        {!autoRenewAvailable && (
          <FormHelperText className="!text-amber-800">
            Automatic renewal is not enabled in Chamber Set Up.
            {value.autoRenew
              ? " Clear the saved auto-renew choice to continue."
              : " It cannot be enabled for this membership change."}
          </FormHelperText>
        )}
        {errors.autoRenew && (
          <FormHelperText error>{errors.autoRenew}</FormHelperText>
        )}
        <FormControlLabel
          sx={fullWidthChoiceControlLabelSx}
          control={
            <Checkbox
              checked={value.emailCommunicationConsentConfirmed}
              disabled={disabled}
              onChange={(event) => onEmailConsentChange(event.target.checked)}
              sx={checkboxControlSx}
            />
          }
          label={
            <span className="block text-base leading-7 text-black">
              I confirm that I have obtained consent from the member contact to send email communications from the Chamber, including updates, event notifications, and member benefits, to the contacts provided. I have made the company aware they can unsubscribe at any time.
            </span>
          }
        />
        {errors.emailCommunicationConsentConfirmed && (
          <FormHelperText error>
            {errors.emailCommunicationConsentConfirmed}
          </FormHelperText>
        )}
    </div>

    <section
      id="total-new-member-invoice"
      className="border-b border-gray-200 pb-10 pt-8 text-black"
    >
      {/* Spec §8: add-ons now go on a SEPARATE full-amount invoice on EVERY
          schedule, so any selected add-ons split the totals into Membership
          Invoice + Add-on Benefits Invoice with the separate-billing callout
          (the old combined annual figure and its "separate line item on
          invoice" caption are gone — no longer true). */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_260px] md:items-center">
        <div>
          <h3 className="text-3xl font-bold leading-tight">
            {membershipAdjustment
              ? "Estimated Membership Billing"
              : "Total New Member Invoice"}
          </h3>
          {membershipAdjustment ? (
            <p className="mt-3 text-lg leading-7">
              This estimate excludes existing add-ons and one-time onboarding
              fees. The server will verify current pricing, invoice, and payment
              history before committing an immediate or scheduled outcome.
            </p>
          ) : isMonthlySchedule ? (
            <p className="mt-3 text-lg leading-7">
              {usesThirdPartyBilling
                ? "Billed on the selected monthly schedule to the responsible third party; the member is not asked for a credit card."
                : monthlyPaymentsAvailable
                ? "Invoiced once for the full membership total; paid in 12 monthly credit-card installments; each payment gets its own receipt."
                : "This member currently has a legacy monthly schedule. Credit card payments are disabled, so select Yearly before saving membership billing changes."}
            </p>
          ) : (
            <p className="mt-3 text-lg leading-7">
              The amount below will be the total amount invoiced to this member plus any applicable tax.
            </p>
          )}
          {showSeparateAddOnInvoice && (
            <p className="mt-2 text-lg leading-7">
              The additional membership benefit add-ons will be invoiced separately from membership.
            </p>
          )}
        </div>
        {showSeparateAddOnInvoice ? (
          <div className="space-y-4 md:text-right">
            <div>
              <p className="text-base font-bold">Membership Invoice</p>
              <p className="text-3xl font-bold">
                {membershipInvoiceTotalLabel}
              </p>
            </div>
            <div>
              <p className="text-base font-bold">Add-on Benefits Invoice</p>
              <p className="text-3xl font-bold">{addOnInvoiceTotalLabel}</p>
            </div>
          </div>
        ) : (
          <p className="text-4xl font-bold md:text-right">
            {membershipAdjustment
              ? membershipInvoiceTotalLabel
              : isMonthlySchedule
                ? membershipInvoiceTotalLabel
                : invoiceTotalLabel}
          </p>
        )}
      </div>
    </section>
    </div>
  );
};

export default MemberBillingStep;
